/**
 * Helpers purs de l'adaptateur CAC (`CacApiService`) : regroupement des mandats en
 * « rattachements », mapping API → VM, filtres client et utilitaires de date.
 * Sans état ni dépendance Angular → fonctions exportées, testables isolément.
 */

import type {
    CommissariatAuxComptes,
    MandatCommissaireAuxComptes,
    PatchedCommissariatAuxComptesPartialUpdateRequest,
    ReserveCommissariatAuxComptes,
} from './models/cac-api.model';
import { apiDateToDate, dateToApi, isoToDate } from '../../../core/date/date-convert';
import { etatRemplacementCac } from './rattachements/cac-remplacement.config';
import type { OpinionCac, StatutMandat, TypeActeNomination, TypeMandataire, TypeRapportCac } from './models/cac.enums';
import type { DocumentExerciceCac, ExerciceCac, MandatCac, MandatDetailleCac, RattachementCac, ReserveCac } from './models/cac.model';

/** Type de document d'exercice accepté par `ajouterDocumentExercice`. */
export type TypeDocumentExercice = TypeRapportCac | 'LETTRE_MISSION' | 'MATRICE_RESERVES' | 'PLAN_ACTION_LEVE_RESERVE';

/** Filtres rattachements (`societe` appliqué côté client). */
export type RattachementCacFilters = {
    societe?: string;
    type_mandataire?: string;
    q?: string;
    page?: number;
    per_page?: number;
    /**
     * Mandats dont l'échéance n'est pas atteinte, au format API. Seul filtre de cette liste qui
     * parte au serveur (`date_fin__gte` sur `/mandats-commissaires-comptes/`) : les autres
     * s'appliquent après regroupement, le rattachement n'existant pas comme ressource (BE-082).
     */
    date_fin__gte?: string;
};

/** Filtres exercices (tout appliqué côté client sauf pagination). */
export type ExerciceCacFilters = {
    societe?: string;
    annee?: number;
    opinion?: OpinionCac;
    q?: string;
    page?: number;
    per_page?: number;
};

/* ── Mapping API → VM ───────────────────────────────────────────────────── */

/** Nom d'une personne physique, vide si absente. */
function nomPersonne(p: { nom: string; prenoms: string } | null): string {
    return p ? `${p.nom} ${p.prenoms}`.trim() : '';
}

/** Nom du titulaire : sa personne physique, à défaut son cabinet. */
export function nomTitulaire(m: MandatCommissaireAuxComptes): string {
    return nomPersonne(m.personne_physique_titulaire) || (m.cabinet_titulaire?.denomination ?? '');
}

/** Nom du suppléant désigné par le même acte ; `null` quand l'acte n'en désigne aucun. */
export function nomSuppleant(m: MandatCommissaireAuxComptes): string | null {
    return nomPersonne(m.personne_physique_suppleant) || m.cabinet_suppleant?.denomination || null;
}

export function toMandatDetaille(m: MandatCommissaireAuxComptes): MandatDetailleCac {
    return {
        id: m.id,
        societe_id: m.societe,
        commissaire_id: m.personne_physique_titulaire?.id ?? null,
        commissaire_nom: nomTitulaire(m),
        cabinet_id: m.cabinet_titulaire?.id ?? null,
        cabinet_nom: m.cabinet_titulaire?.denomination ?? '',
        suppleant_personne_id: m.personne_physique_suppleant?.id ?? null,
        date_designation: m.date_designation,
        date_fin: m.date_fin,
        periode_exercice: m.periode_exercice,
        rang: m.rang,
        suppleant_nom: nomSuppleant(m),
    };
}

/**
 * Un mandat porte la paire : il vaut une ligne. Les mandats ne se regroupent plus par porteur —
 * le serveur limite le nombre de mandats titulaires actifs par société, et fusionner deux mandats
 * sur une ligne rendait ce décompte incompréhensible. Les reconductions se lisent au rang, et
 * l'historique par la bascule « Afficher les mandats échus ».
 */
export function toRattachement(mandat: MandatCommissaireAuxComptes): RattachementCac {
    const dernier = mandat;
    const remplacantNom = nomPersonne(dernier.personne_physique_titulaire_remplacant) || null;
    return {
        identifiant: dernier.id,
        societe_id: dernier.societe,
        commissaire_aux_comptes_id: dernier.personne_physique_titulaire?.id ?? '',
        cabinet_expert_comptable_id: dernier.cabinet_titulaire?.id ?? '',
        type_mandataire: (dernier.type_mandataire_titulaire?.code ?? 'CABINET') as TypeMandataire,
        date_designation: dernier.date_designation,
        date_fin_mandat: dernier.date_fin,
        periode_exercice: dernier.periode_exercice,
        // Pas de repli sur « DECRET » : inventer un acte de nomination absent de la donnée
        // afficherait un type faux. Vide → « — » côté écran.
        type_acte_nomination: (dernier.type_acte_nomination?.code ?? '') as TypeActeNomination,
        reference_acte_nomination: dernier.reference_acte_nomination,
        document_nom: dernier.acte_nomination?.name ?? null,
        date_creation_rattachement: dernier.created_at,
        remplacant_nom: remplacantNom,
        remplacant_id: dernier.personne_physique_titulaire_remplacant?.id ?? null,
        date_debut_remplacement: dernier.date_debut_remplacement,
        motif_modification: dernier.motif_modification || null,
        rang: dernier.rang,
        nombre_exercices_restants: dernier.nombre_exercices_restants,
        date_designation_mandat: dernier.date_designation,
        commissaire_nom: nomTitulaire(dernier),
        cabinet_nom: dernier.cabinet_titulaire?.denomination ?? '',
        ...partSuppleant(dernier),
        remplacable: etatRemplacementCac({ date_fin_mandat: dernier.date_fin, remplacant_nom: remplacantNom }).remplacable,
    };
}

/** Champs du suppléant d'un rattachement — vides quand l'acte n'en désigne aucun. */
function partSuppleant(
    m: MandatCommissaireAuxComptes,
): Pick<
    RattachementCac,
    | 'suppleant_personne_id'
    | 'suppleant_cabinet_id'
    | 'suppleant_nom'
    | 'suppleant_cabinet_nom'
    | 'suppleant_type_mandataire'
    | 'suppleant_remplacant_nom'
    | 'suppleant_remplacant_id'
> {
    return {
        suppleant_personne_id: m.personne_physique_suppleant?.id ?? '',
        suppleant_cabinet_id: m.cabinet_suppleant?.id ?? '',
        suppleant_nom: nomSuppleant(m),
        suppleant_cabinet_nom: m.cabinet_suppleant?.denomination ?? '',
        suppleant_type_mandataire: (m.type_mandataire_suppleant?.code ?? null) as TypeMandataire | null,
        suppleant_remplacant_nom: nomPersonne(m.personne_physique_suppleant_remplacant) || null,
        suppleant_remplacant_id: m.personne_physique_suppleant_remplacant?.id ?? null,
    };
}

export function toMandatCac(m: MandatCommissaireAuxComptes, rattachementId: string): MandatCac {
    const future = joursAvant(m.date_fin, new Date()) >= 0;
    const statut: StatutMandat = future ? 'EN_COURS' : 'NON_RENOUVELE';
    return {
        id_mandat: m.id,
        rattachement_id: rattachementId,
        statut_mandat: statut,
        nombre_exercice_restant: m.nombre_exercices_restants ?? 0,
        date_debut_mandat: m.date_designation,
        date_fin_mandat: m.date_fin,
        periode_mandat: m.periode_exercice,
        rang_mandat: m.rang,
        cac_remplacant_id: m.personne_physique_titulaire_remplacant?.id ?? null,
        remplacant_nom: nomPersonne(m.personne_physique_titulaire_remplacant) || null,
        suppleant_remplacant_nom: nomPersonne(m.personne_physique_suppleant_remplacant) || null,
        date_debut_remplacement: m.date_debut_remplacement,
        motif_modification: m.motif_modification || null,
        document_nom: m.acte_nomination?.name ?? null,
        type_acte_nomination: (m.type_acte_nomination?.code ?? null) as TypeActeNomination | null,
        reference_acte_nomination: m.reference_acte_nomination,
    };
}

export function toExercice(c: CommissariatAuxComptes): ExerciceCac {
    return {
        identifiant: c.id,
        societe_id: c.societe,
        annee_exercice: c.annee_debut,
        opinion_formulee: (c.opinion_formulee?.code ?? null) as ExerciceCac['opinion_formulee'],
        observation: c.observation ?? null,
        commissaires_actifs_ids: [],
        date_creation_exercice: c.created_at,
        nb_reserves: c.reserves?.length ?? 0,
        reserves_synthese: (c.reserves ?? []).map((r) => ({
            categorie_libelle: r.categorie?.label ?? 'Non catégorisée',
            recurrence: !!r.recurrence,
        })),
        valide: c.valide ?? false,
    };
}

/**
 * Identifiants des mandats actifs sur l'exercice d'année `annee`
 * pour la société `societeId` : même société et année comprise dans la période d'activité du
 * rattachement, soit [année(date_designation) … année(date_fin)] inclus (bornes ouvertes si la
 * date correspondante est absente/illisible). Dérivation purement client (aucun lien backend).
 */
export function rattachementsActifs(mandats: MandatCommissaireAuxComptes[], societeId: string, annee: number): string[] {
    const ids: string[] = [];
    for (const mandat of mandats) {
        const rat = toRattachement(mandat);
        if (rat.societe_id !== societeId) {
            continue;
        }
        const debut = anneeDe(rat.date_designation);
        const fin = anneeDe(rat.date_fin_mandat);
        if ((debut === null || annee >= debut) && (fin === null || annee <= fin)) {
            ids.push(rat.identifiant);
        }
    }
    return ids;
}

export function toReserve(r: ReserveCommissariatAuxComptes): ReserveCac {
    return {
        identifiant: r.id,
        exercice_cac_id: r.commissariat_aux_comptes ?? '',
        intitule_reserve: r.intitule,
        reformulation: r.reformulation || null,
        montant: r.montant ?? null,
        categorie: r.categorie?.code ?? null,
        categorie_libelle: r.categorie?.label ?? null,
        recurrence: r.recurrence,
        date_creation_reserve: r.created_at,
    };
}

export function buildDocs(c: CommissariatAuxComptes): DocumentExerciceCac[] {
    const docs: DocumentExerciceCac[] = [];
    if (c.lettre_mission) {
        docs.push(toDoc(c.id, 'LETTRE_MISSION', c.lettre_mission.name, c.lettre_mission.id, c.lettre_mission.created_at));
    }
    if (c.matrice_reserve) {
        docs.push(toDoc(c.id, 'MATRICE_RESERVES', c.matrice_reserve.name, c.matrice_reserve.id, c.matrice_reserve.created_at));
    }
    if (c.plan_action_levee_reserve) {
        docs.push(
            toDoc(
                c.id,
                'PLAN_ACTION_LEVE_RESERVE',
                c.plan_action_levee_reserve.name,
                c.plan_action_levee_reserve.id,
                c.plan_action_levee_reserve.created_at,
            ),
        );
    }
    for (const f of c.documents_rapport_elaboration ?? []) {
        // Le `type_document` de ces fichiers est détourné à l'upload (pas de code dédié) :
        // on les classe en « rapport d'élaboration » générique d'après leur slot d'appartenance.
        docs.push(toDoc(c.id, 'RAPPORT_AUTRE', f.name, f.id, f.created_at));
    }
    return docs;
}

export function toDoc(exerciceId: string, type: TypeDocumentExercice, nom: string, id: string, date: string): DocumentExerciceCac {
    return {
        identifiant: id,
        exercice_cac_id: exerciceId,
        type_rapport: type,
        nom_fichier: nom,
        date_chargement: date,
    };
}

/* ── Documents : slots commissariat ─────────────────────────────────────── */

/** Code de document réel pour `/files/` selon le slot logique de l'exercice. */
export function codeDocumentReel(type: TypeDocumentExercice): string {
    switch (type) {
        case 'LETTRE_MISSION':
            return 'LETTRE_MISSION';
        case 'MATRICE_RESERVES':
            return 'MATRICE_RESERVE';
        case 'PLAN_ACTION_LEVE_RESERVE':
            return 'PLAN_ACTION_LEVEE_RESERVE';
        default:
            // Rapports d'élaboration : pas de code dédié au référentiel (Phase 3).
            return 'LETTRE_MISSION';
    }
}

/** Libellé affiché pour un rapport d'élaboration : aucune valeur dédiée au référentiel. */
export const LIBELLE_RAPPORT_ELABORATION = 'Rapport d’élaboration';

/**
 * Libellé d'un document d'exercice. Les trois slots (lettre de mission, matrice, plan d'action)
 * prennent le libellé du référentiel `TYPE_DOCUMENT_COMMISSARIAT_AUX_COMPTES` par leur code réel,
 * à défaut ce code ; les rapports d'élaboration n'ont pas de valeur au référentiel.
 */
export function libelleDocumentExercice(type: TypeDocumentExercice, libelles: ReadonlyMap<string, string>): string {
    if (type !== 'LETTRE_MISSION' && type !== 'MATRICE_RESERVES' && type !== 'PLAN_ACTION_LEVE_RESERVE') {
        return LIBELLE_RAPPORT_ELABORATION;
    }
    const codeReel = codeDocumentReel(type);
    return libelles.get(codeReel) ?? codeReel;
}

/** Patch du commissariat rattachant le fichier `fileId` au slot correspondant. */
export function patchPourSlot(
    type: TypeDocumentExercice,
    fileId: string,
    commissariat: CommissariatAuxComptes,
): PatchedCommissariatAuxComptesPartialUpdateRequest {
    switch (type) {
        case 'LETTRE_MISSION':
            return { lettre_mission_id: fileId };
        case 'MATRICE_RESERVES':
            return { matrice_reserve_id: fileId };
        case 'PLAN_ACTION_LEVE_RESERVE':
            return { plan_action_levee_reserve_id: fileId };
        default: {
            const existants = (commissariat.documents_rapport_elaboration ?? []).map((f) => f.id);
            return { documents_rapport_elaboration_ids: [...existants, fileId] };
        }
    }
}

/* ── Filtres client ─────────────────────────────────────────────────────── */

/**
 * Filtres restés côté client : la société part désormais au serveur, et la qualité a disparu du
 * contrat. Ne demeurent ici que la nature du mandataire et la recherche libre, que l'API n'expose pas.
 */
export function matchRattachement(r: RattachementCac, f: RattachementCacFilters): boolean {
    if (f.type_mandataire && r.type_mandataire !== f.type_mandataire) {
        return false;
    }
    if (f.q) {
        const q = f.q.toLowerCase();
        // Les noms d'abord : c'est un commissaire qu'on cherche, pas un identifiant de mandat.
        // La société n'y figure pas — le rattachement n'en porte que l'UUID, et le filtre
        // déroulant « Société » la couvre déjà.
        const hay = [
            r.commissaire_nom,
            r.cabinet_nom,
            r.suppleant_nom,
            r.suppleant_cabinet_nom,
            r.remplacant_nom,
            r.suppleant_remplacant_nom,
            r.reference_acte_nomination,
            r.periode_exercice,
            r.identifiant,
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        if (!hay.includes(q)) {
            return false;
        }
    }
    return true;
}

export function matchExercice(e: ExerciceCac, f: ExerciceCacFilters): boolean {
    if (f.societe && e.societe_id !== f.societe) {
        return false;
    }
    if (f.annee && e.annee_exercice !== f.annee) {
        return false;
    }
    if (f.opinion && e.opinion_formulee !== f.opinion) {
        return false;
    }
    if (f.q && !e.identifiant.toLowerCase().includes(f.q.toLowerCase())) {
        return false;
    }
    return true;
}

/* ── Dates ──────────────────────────────────────────────────────────────── */

/** Date au format API (`YYYY-MM-DD`) pour les filtres serveur. */
export function toApiDate(d: Date): string {
    // Heure locale : toISOString() (UTC) décalait d'un jour hors UTC (audit R-16).
    return dateToApi(d) ?? '';
}

/** Année d'une date (`YYYY-MM-DD` ou `DD-MM-YYYY`) ; `null` si absente ou illisible. */
export function anneeDe(dateStr: string | null | undefined): number | null {
    const d = parseDate(dateStr);
    return d ? d.getFullYear() : null;
}

/** Jours avant `dateStr` depuis `ref` ; tolère `YYYY-MM-DD` et `DD-MM-YYYY`. */
export function joursAvant(dateStr: string, ref: Date): number {
    const d = parseDate(dateStr);
    if (!d) {
        return Number.POSITIVE_INFINITY;
    }
    return Math.ceil((d.getTime() - ref.getTime()) / 86_400_000);
}

export function parseDate(s: string | null | undefined): Date | null {
    if (!s) {
        return null;
    }
    // Heure locale et jour existant : new Date('AAAA-MM-JJ') est lu en UTC et accepte le 31/02 (audit R-16).
    return /^\d{2}-\d{2}-\d{4}$/.test(s) ? apiDateToDate(s) : isoToDate(s);
}
